import { useCallback, useEffect, useRef, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPostForm, apiPut, prettifyApiError } from "@/shared/api";

export type SyncStats = {
  queued: number;
  running: number;
  succeeded_24h: number;
  failed_24h: number;
};

export type SyncJob = {
  id: number;
  module: string;
  job_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  scheduled_at: string;
  finished_at: string | null;
  error: string | null;
};

export type SyncInterval = {
  module: string;
  interval_seconds: number;
  enabled: boolean;
  last_enqueued_at: string | null;
};

export type SystemInfo = {
  app_name: string;
  version: string;
  env: string;
  platform: string;
  python_version: string;
  data_dir: string;
  log_dir: string;
  poleepo_base_url: string;
  // Audit 2026-05-14: stato runtime delle migration startup. Quando
  // ``migration_status === "ahead"`` significa che il DB ha una revision
  // sconosciuta al rilascio in esecuzione (immagine piu' vecchia del DB).
  // Il frontend mostra il banner ``OutdatedAppBanner``: niente da
  // installare, si ricarica dopo il deploy dell'immagine aggiornata.
  migration_status?: "ok" | "ahead" | "error" | "unknown";
  migration_detail?: string;
};

export type AccountInfo = {
  email: string;
  organization: string;
  vat_number: string;
  language: string;
  plan: string;
};

export type CredentialMeta = {
  key: string;
  label: string;
  keyring_name: string;
  created_at: string;
};

export type CredentialWrite = {
  key:
    | "poleepo_token"
    | "poleepo_client_id"
    | "poleepo_client_secret"
    | "imap_hostinger_password"
    | "thunderbird_path"
    | "claude_api_key"
    | "opencode_go_api_key"
    | "am_reader_password";
  label: string;
  value: string;
};

export type AllServiceSettings = {
  template_path: string;
  template_configured: boolean;
  template_exists: boolean;
  /** Template XLSX caricato nel DB (byte): fonte usata sul web quando il file
   *  locale non esiste. Caricato via "Carica template" nelle Impostazioni. */
  template_blob_present: boolean;
  template_blob_filename: string;
  /** Path Thunderbird usato per aprire la finestra compose con allegati AS.
   *  Default Windows: C:\Program Files\Mozilla Thunderbird\thunderbird.exe.
   *  Sostituisce la vecchia config SMTP (rimossa 2026-05-12). */
  thunderbird_path: string;
  thunderbird_configured: boolean;
  thunderbird_exists: boolean;
};

export type LogTail = {
  file: string;
  lines: string[];
  truncated: boolean;
};

export type MailAccount = {
  id: number;
  source: "mirakl" | "amazon" | "metro" | "manomano";
  slug: string;
  label: string;
  enabled: boolean;
};

export type MailAccountWrite = {
  source: MailAccount["source"];
  slug: string;
  label: string;
  base_url: string;
  enabled: boolean;
  config: Record<string, string | number>;
};

export const emptyStats: SyncStats = {
  queued: 0,
  running: 0,
  succeeded_24h: 0,
  failed_24h: 0,
};

type RefreshState<T> = {
  value: T;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useSyncDashboard(): {
  stats: SyncStats;
  jobs: SyncJob[];
  isRefreshing: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  retryJob: (id: number) => Promise<void>;
  cancelJob: (id: number) => Promise<void>;
  pendingIds: Set<number>;
} {
  const [stats, setStats] = useState<SyncStats>(emptyStats);
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const inFlight = useRef<AbortController | null>(null);
  // Marca chi ha scritto ``error``: se viene da "Riprova"/"Annulla" il polling
  // (ogni 3s) non deve azzerarlo, altrimenti il motivo del fallimento sparisce
  // prima che l'operatore lo legga e il tasto sembra non fare niente.
  const errorFromAction = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setIsRefreshing(true);
    try {
      const [nextStats, nextJobs] = await Promise.all([
        apiGet<SyncStats>("/api/sync/stats", { signal: controller.signal }),
        apiGet<SyncJob[]>("/api/sync/jobs?limit=20", { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setStats(nextStats);
      setJobs(nextJobs);
      if (!errorFromAction.current) setError(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      errorFromAction.current = false;
      setError(prettifyApiError(err));
    } finally {
      // Spegni lo spinner SOLO se questa e' ancora la richiesta corrente: una
      // richiesta soppressa (abortita da una piu' recente) dichiarava "finito"
      // mentre la nuova era ancora in volo, quindi il tasto tornava "Aggiorna"
      // con i dati vecchi a schermo. Stesso pattern di messaggi/api.ts.
      if (inFlight.current === controller) {
        inFlight.current = null;
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Audit 2026-05-11 M15: pausa il polling quando il documento e' hidden
    // (tab in background, window minimizzata). Senza, l'app desktop
    // continua a poll 3s anche quando l'utente sta lavorando su altre
    // finestre, sprecando CPU/batteria/rumore nei log backend.
    // ``setTimeout`` a catena: il giro dopo parte DALLA risposta. L'``abort()``
    // qui sopra spegne la fetch nel browser ma NON ferma l'handler FastAPI,
    // che continua fino in fondo tenendosi la connessione del pool. Con
    // l'``setInterval`` a 3 s su ``/api/sync/stats`` — che il 2026-09-08
    // rispondeva in 13 s di media, fino a 540 — il server accumulava quattro
    // giri sovrapposti per ogni tab aperta, invisibili dal lato client.
    // 10 s, non 3: questa e' una pagina di STATO, non un cruscotto in tempo
    // reale, e ogni giro sono due chiamate (`/api/sync/stats` + `/api/sync/jobs`).
    // Misurato il 2026-09-09: da sola faceva 155 richieste su 275 arrivate al
    // backend, il 56% del traffico, per aggiornare numeri che cambiano ogni
    // qualche minuto. Il tasto «Aggiorna» resta per chi ha fretta.
    const POLL_STATO_MS = 10_000;
    let timer: number | null = null;
    let attivo = false;
    const startPolling = () => {
      if (attivo) return;
      attivo = true;
      const giro = async (): Promise<void> => {
        await refresh();
        if (attivo) timer = window.setTimeout(() => void giro(), POLL_STATO_MS);
      };
      timer = window.setTimeout(() => void giro(), POLL_STATO_MS);
    };
    const stopPolling = () => {
      attivo = false;
      if (timer === null) return;
      window.clearTimeout(timer);
      timer = null;
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Riprende subito + restart interval per non aspettare 3s.
        void refresh();
        startPolling();
      }
    };
    if (!document.hidden) startPolling();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      stopPolling();
      inFlight.current?.abort();
    };
  }, [refresh]);

  const withPending = useCallback(
    async (id: number, op: () => Promise<unknown>) => {
      setPendingIds((prev) => new Set(prev).add(id));
      // Nuova azione: l'esito precedente non vale piu', il polling torna
      // padrone di ``error`` finche' questa non fallisce a sua volta.
      errorFromAction.current = false;
      try {
        await op();
        await refresh();
        setError(null);
      } catch (err) {
        errorFromAction.current = true;
        setError(prettifyApiError(err));
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [refresh],
  );

  const retryJob = useCallback(
    (id: number) => withPending(id, () => apiPost<SyncJob>(`/api/sync/jobs/${id}/retry`)),
    [withPending],
  );
  const cancelJob = useCallback(
    (id: number) => withPending(id, () => apiPost<SyncJob>(`/api/sync/jobs/${id}/cancel`)),
    [withPending],
  );

  return { stats, jobs, isRefreshing, error, refresh, retryJob, cancelJob, pendingIds };
}

export function useResource<T>(path: string, fallback: T): RefreshState<T> {
  const [value, setValue] = useState<T>(fallback);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setIsLoading(true);
    try {
      const next = await apiGet<T>(path, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setValue(next);
      setError(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(prettifyApiError(err));
    } finally {
      // Vedi useSyncDashboard: solo la richiesta corrente puo' spegnere lo
      // spinner, altrimenti un cambio di ``path`` fa leggere come definitivo
      // il ``fallback`` mentre la nuova richiesta e' ancora in volo.
      if (inFlight.current === controller) {
        inFlight.current = null;
        setIsLoading(false);
      }
    }
  }, [path]);

  useEffect(() => {
    void refresh();
    return () => inFlight.current?.abort();
  }, [refresh]);

  return { value, isLoading, error, refresh };
}

export async function updateInterval(
  module: string,
  patch: Partial<Pick<SyncInterval, "enabled" | "interval_seconds">>,
): Promise<SyncInterval> {
  return apiPut<SyncInterval>(`/api/sync/intervals/${module}`, patch);
}

export async function runIntervalNow(module: string): Promise<SyncJob> {
  return apiPost<SyncJob>(`/api/sync/intervals/${module}/run-now`);
}

export async function createMailAccount(payload: MailAccountWrite): Promise<MailAccount> {
  return apiPost<MailAccount>("/api/mail/accounts", payload);
}

export async function saveCredential(payload: CredentialWrite): Promise<CredentialMeta> {
  return apiPost<CredentialMeta>("/api/credentials", payload);
}

export async function updateAllServiceSettings(templatePath: string): Promise<AllServiceSettings> {
  return apiPut<AllServiceSettings>("/api/suppliers/allservice/settings", {
    template_path: templatePath,
  });
}

/** Carica il MASTER XLSX AllService nel DB (necessario sul web: il file locale
 *  del PC non esiste sul server). */
export async function uploadAllServiceTemplate(file: File): Promise<AllServiceSettings> {
  const form = new FormData();
  form.append("file", file);
  return apiPostForm<AllServiceSettings>("/api/suppliers/allservice/template-file", form);
}

export type CosmaSettings = {
  host: string;
  port: number;
  partita_iva: string;
  ftp_user: string;
  remote_dir: string;
  sku_prefixes: string[];
  /** Solo metadata: dice SE la password FTP e' nel keyring, mai il valore. */
  password_configured: boolean;
  /** Quanti SKU Cosma sono caricati nel catalogo (catalog.supplier_catalog_entries,
   *  supplier_id='cosma', importati dal Google Sheet "Listino Cosma"). Il
   *  sistema riconosce i prodotti Cosma per appartenenza esatta a questo
   *  set (precedenza sui prefissi). 0 = catalogo non popolato. */
  sku_count: number;
};

export type CosmaSettingsWrite = {
  host: string;
  port: number;
  partita_iva: string;
  ftp_user: string;
  remote_dir: string;
  sku_prefixes: string[];
};

export async function saveCosmaSettings(payload: CosmaSettingsWrite): Promise<CosmaSettings> {
  return apiPut<CosmaSettings>("/api/suppliers/cosma/settings", payload);
}

export async function saveCosmaCredentials(password: string): Promise<CosmaSettings> {
  return apiPut<CosmaSettings>("/api/suppliers/cosma/credentials", { password });
}

export async function resetCache(olderThanDays: number = 30): Promise<void> {
  // Audit 2026-05-29 [LOW]: niente fetch reimplementato a mano. Si riusa il
  // wrapper condiviso ``apiDelete`` (stesso ``fetchWithContext`` +
  // ``raiseApiError`` degli altri verbi), che da' errori col formato
  // path-context uniforme ed e' compatibile col renderer packaged (file://).
  // ``apiDelete`` ritorna ``void`` (pensato per i 204 No Content), quindi il
  // body con i contatori non viene esposto: il feedback di successo lato UI
  // resta generico (la pulizia e' comunque andata a buon fine).
  await apiDelete(`/api/system/cache?older_than_days=${olderThanDays}`);
}
